import { Edit, Plus, Trash2 } from "lucide-react";
import Sidebar from "../components/AdminSidebar";
import "../styles/AdminMessages.css";
import { useEffect, useState } from "react";
import MessageModal from "../components/MessageModal";
import EditMessageModal from "../components/EditMessageModal";
import EditUserMessageModal from "../components/EditUserMessageModal";

export default function AdminMessages() {
  // Powiadomienia systemowe
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Wiadomości od użytkowników (backend do podpięcia)
  const [userMessages, setUserMessages] = useState([]);
  const [userLoading, setUserLoading] = useState(true);

  // Modale
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  // User message modal state
  const [showUserMessageModal, setShowUserMessageModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userMessageUsers, setUserMessageUsers] = useState([]);
  const [userMessageSubject, setUserMessageSubject] = useState('');
  const [userMessageContent, setUserMessageContent] = useState('');
  const [userMessageType, setUserMessageType] = useState('info');
  const [userMessageSearchTerm, setUserMessageSearchTerm] = useState('');

  // ===== API – powiadomienia systemowe =====
  const fetchMessages = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/messages/`
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        setMessages([]);
      }
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== API – wiadomości od użytkowników (do spięcia) =====
  const fetchUserMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/messages/user`,
        {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUserMessages(data);
      } else {
        // Endpoint might not exist yet, set empty array
        setUserMessages([]);
      }
    } catch (error) {
      setUserMessages([]);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    // Get current user ID from token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.id);
      } catch (error) {
        // Error parsing token
      }
    }

    fetchMessages();
    fetchUserMessages();
    
    // Fetch all users for user message modal
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/users`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAllUsers(data);
        }
      } catch (error) {
        // Error fetching users
      }
    };
    fetchUsers();
  }, []);

  const handleMessageCreated = () => fetchMessages();
  const handleMessageUpdated = () => fetchMessages();

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tę wiadomość?")) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/messages/${id}`,
        { method: "DELETE" }
      );
      if (res.ok) fetchMessages();
    } catch (err) {
      // Error deleting message
    }
  };

  // ===== Akcje dla wiadomości od użytkowników =====
  const handleUserDelete = async (id) => {
    if (
      !window.confirm("Czy na pewno chcesz usunąć wiadomość od użytkownika?")
    )
      return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/messages/user/${id}`,
        { method: "DELETE" }
      );
      if (res.ok) fetchUserMessages();
    } catch (err) {
      // Error deleting user message
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <Sidebar />

      {/* Główna kolumna */}
      <div className="main-content">
        {/* Tytuł sekcji u góry (po lewej) */}
        <div className="header">
          <h1 className="header-title">Wiadomości systemowe</h1>
        </div>

        {/* Wewnętrzny kontener jak na innych stronach */}
        <div className="messages-page-inner">
          {/* Nagłówek strony „Panel wiadomości” */}
          <header className="messages-page-header">
            <h2 className="messages-page-title">Panel wiadomości</h2>
            <p className="messages-page-subtitle">
              Wyślij wiadomość wewnętrzną do kursantów oraz zarządzaj wiadomościami.
            </p>
          </header>

          {/* Jedna biała karta z dwiema sekcjami (jak na screenie) */}
          <div className="management-content messages-card">
            {/* Sekcja: powiadomienia globalne/grupowe */}
            <div className="management-card">
              <div className="card-header-management">
                <div>
                  <h3>Wiadomości globalne i grupowe</h3>
                </div>
                <div className="button-container">
                  <button
                    className="btn btn-primary"
                    onClick={() => setIsModalOpen(true)}
                    type="button"
                  >
                    <Plus size={16} className="icon-style" />
                    Utwórz Nową Wiadomość
                  </button>
                </div>
              </div>

              {loading ? (
                <p>Ładowanie wiadomości...</p>
              ) : (
                <table className="message-table">
                  <thead>
                    <tr>
                      <th>Tytuł</th>
                      <th>Treść</th>
                      <th>Typ</th>
                      <th>Odbiorcy</th>
                      <th>Utworzono</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = messages.filter(msg => {
                        // Show ALL global/group messages (these are broadcast/received messages)
                        const userCount = parseInt(msg.user_count) || 0;
                        const hasGroups = msg.groups && msg.groups.trim && msg.groups.trim().length > 0;
                        const isGlobalOrGroup = msg.is_global === true || (hasGroups && userCount === 0);
                        // Show all global/group messages regardless of sender (they are broadcast messages)
                        return isGlobalOrGroup;
                      });
                      return filtered.length > 0 ? (
                        filtered.map((msg) => (
                        <tr key={msg.message_id}>
                          <td data-label="Tytuł">{msg.title}</td>
                          <td data-label="Treść">{msg.content}</td>
                          <td data-label="Typ">
                            <span
                              className={`badge badge-${msg.message_type}`}
                            >
                              {msg.message_type === "info"
                                ? "Informacja"
                                : msg.message_type === "warning"
                                ? "Ważne"
                                : "Alert"}
                            </span>
                          </td>
                          <td data-label="Odbiorcy">
                            {msg.is_global ? "Wszyscy" : msg.groups || "-"}
                          </td>
                          <td data-label="Utworzono">
                            {msg.created_at
                              ? new Date(msg.created_at).toLocaleString(
                                  "pl-PL",
                                  {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )
                              : "-"}
                          </td>
                          <td
                            data-label="Akcje"
                            className="message-actions"
                          >
                            <button
                              className="btn-icon edit"
                              onClick={() => {
                                setSelectedMessage(msg);
                                setIsEditModalOpen(true);
                              }}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn-icon delete"
                              onClick={() =>
                                handleDelete(msg.message_id)
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                      ) : (
                        <tr>
                          <td colSpan="6">
                            Brak wiadomości do wyświetlenia.
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              )}
            </div>

            {/* Sekcja: powiadomienia do użytkowników - tylko wysłane przez obecnego użytkownika */}
            <div className="management-card user-messages-card">
              <div className="card-header-management">
                <div>
                  <h3>Wiadomości wewnętrzne do użytkowników</h3>
                </div>
                <div className="button-container">
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowUserMessageModal(true)}
                    type="button"
                  >
                    <Plus size={16} className="icon-style" />
                    Wyślij do użytkowników
                  </button>
                </div>
              </div>

              {loading ? (
                <p>Ładowanie wiadomości...</p>
              ) : (
                <table className="message-table">
                  <thead>
                    <tr>
                      <th>Tytuł</th>
                      <th>Treść</th>
                      <th>Typ</th>
                      <th>Odbiorcy</th>
                      <th>Utworzono</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = messages.filter(msg => {
                        const userCount = parseInt(msg.user_count) || 0;
                        return currentUserId && parseInt(msg.created_by) === currentUserId && userCount > 0;
                      });
                      return filtered.length > 0 ? (
                        filtered.map((msg) => (
                        <tr key={msg.message_id}>
                          <td data-label="Tytuł">{msg.title}</td>
                          <td data-label="Treść">{msg.content}</td>
                          <td data-label="Typ">
                            <span
                              className={`badge badge-${msg.message_type}`}
                            >
                              {msg.message_type === "info"
                                ? "Informacja"
                                : msg.message_type === "warning"
                                ? "Ważne"
                                : "Alert"}
                            </span>
                          </td>
                          <td data-label="Odbiorcy">
                            {msg.recipient_users && msg.recipient_users.length > 0 ? (
                              <div style={{ fontSize: '0.875rem' }}>
                                {msg.recipient_users.length === 1 
                                  ? msg.recipient_users[0].full_name || msg.recipient_users[0].username
                                  : `${msg.recipient_users.length} użytkowników`}
                                {msg.recipient_users.length <= 3 && (
                                  <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#64748b' }}>
                                    {msg.recipient_users.map(u => u.full_name || u.username).join(', ')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              `${msg.user_count || 0} użytkowników`
                            )}
                          </td>
                          <td data-label="Utworzono">
                            {msg.created_at
                              ? new Date(msg.created_at).toLocaleString(
                                  "pl-PL",
                                  {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )
                              : "-"}
                          </td>
                          <td
                            data-label="Akcje"
                            className="message-actions"
                          >
                            <button
                              className="btn-icon edit"
                              onClick={() => {
                                setSelectedMessage(msg);
                                setIsEditUserModalOpen(true);
                              }}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn-icon delete"
                              onClick={() =>
                                handleDelete(msg.message_id)
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                      ) : (
                        <tr>
                          <td colSpan="6">
                            Brak wiadomości do wyświetlenia.
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              )}
            </div>

            {/* Sekcja: wiadomości od użytkowników */}
            <div className="management-card user-messages-card">
              <div className="card-header-management">
                <div>
                  <h3>Wiadomości wewnętrzne od użytkowników</h3>
                </div>
              </div>

              {userLoading ? (
                <p>Ładowanie wiadomości od użytkowników...</p>
              ) : (
                <table className="message-table">
                  <thead>
                    <tr>
                      <th>Tytuł</th>
                      <th>Treść</th>
                      <th>Typ</th>
                      <th>Użytkownik</th>
                      <th>Kiedy wysłana</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userMessages.length > 0 ? (
                      userMessages.map((umsg) => (
                        <tr key={umsg.id}>
                          <td data-label="Tytuł">{umsg.title}</td>
                          <td data-label="Treść">{umsg.content}</td>
                          <td data-label="Typ wiadomości">
                            {umsg.message_type ? (
                              <span
                                className={`badge badge-${umsg.message_type}`}
                              >
                                {umsg.message_type === "info"
                                  ? "Informacja"
                                  : umsg.message_type === "warning"
                                  ? "Ważne"
                                  : "Alert"}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td data-label="Użytkownik">
                            {umsg.user_type === 'administrator' 
                              ? umsg.username || umsg.user_name
                              : umsg.user_name}
                            {umsg.user_email && umsg.user_type !== 'administrator'
                              ? ` (${umsg.user_email})`
                              : ""}
                          </td>
                          <td data-label="Kiedy wysłana">
                            {umsg.sent_at
                              ? new Date(
                                  umsg.sent_at
                                ).toLocaleString("pl-PL", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "-"}
                          </td>
                          <td
                            data-label="Akcje"
                            className="message-actions"
                          >
                            <button
                              className="btn-icon delete"
                              onClick={() =>
                                handleUserDelete(umsg.id)
                              }
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6">
                          Brak wiadomości od użytkowników do
                          wyświetlenia.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Modale */}
        <MessageModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onMessageCreated={handleMessageCreated}
        />

        {selectedMessage && (
          <>
            <EditMessageModal
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setSelectedMessage(null);
              }}
              message={selectedMessage}
              onMessageUpdated={handleMessageUpdated}
            />
            <EditUserMessageModal
              isOpen={isEditUserModalOpen}
              onClose={() => {
                setIsEditUserModalOpen(false);
                setSelectedMessage(null);
              }}
              message={selectedMessage}
              onMessageUpdated={handleMessageUpdated}
            />
          </>
        )}

        {/* User Message Modal - Send message to specific users */}
        {showUserMessageModal && (
          <div className='user-selector-modal-overlay' onClick={() => setShowUserMessageModal(false)}>
            <div className='user-message-modal' onClick={(e) => e.stopPropagation()}>
              <div className='user-selector-header'>
                <h3>Wyślij wiadomość wewnętrzną do użytkowników</h3>
                <button 
                  className='user-selector-close'
                  onClick={() => {
                    setShowUserMessageModal(false);
                    setUserMessageUsers([]);
                    setUserMessageSubject('');
                    setUserMessageContent('');
                    setUserMessageType('info');
                    setUserMessageSearchTerm('');
                  }}
                >
                  ×
                </button>
              </div>

              <div className='user-message-modal-content'>
                {/* User Selection */}
                <div className='user-message-section'>
                <div className='user-selector-search'>
                  <input
                    type='text'
                    placeholder='Szukaj użytkownika...'
                    value={userMessageSearchTerm}
                    onChange={(e) => setUserMessageSearchTerm(e.target.value)}
                  />
                </div>
                <div className='user-selector-list' style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {allUsers
                    .filter(user => {
                      const searchLower = userMessageSearchTerm.toLowerCase();
                      return !userMessageSearchTerm || 
                        user.full_name?.toLowerCase().includes(searchLower) ||
                        user.username?.toLowerCase().includes(searchLower) ||
                        user.email?.toLowerCase().includes(searchLower);
                    })
                    .map(user => (
                      <label key={user.user_id} className='user-selector-item'>
                        <input
                          type='checkbox'
                          checked={userMessageUsers.includes(user.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUserMessageUsers([...userMessageUsers, user.user_id]);
                            } else {
                              setUserMessageUsers(userMessageUsers.filter(id => id !== user.user_id));
                            }
                          }}
                        />
                        <div className='user-selector-item-info'>
                          <div className='user-selector-item-name'>{user.full_name || user.username}</div>
                          <div className='user-selector-item-details'>
                            {user.email && <span>{user.email}</span>}
                            <span className={`user-type-badge user-type-${user.user_type}`}>
                              {user.user_type === 'student' ? 'Kursant' : 
                               user.user_type === 'lecturer' ? 'Wykładowca' : 
                               user.user_type === 'administrator' ? 'Administrator' : user.user_type}
                            </span>
                            {user.group_name && <span>Grupa: {user.group_name}</span>}
                          </div>
                        </div>
                      </label>
                    ))}
                </div>
                {userMessageUsers.length > 0 && (
                  <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666', padding: '0.5rem', background: '#f8fafc', borderRadius: '6px' }}>
                    Wybrano: {userMessageUsers.length} {userMessageUsers.length === 1 ? 'użytkownika' : 'użytkowników'}
                  </div>
                )}
              </div>


              {/* Message Type */}
              <div className='user-message-section'>
                <label className='form-label'>Typ wiadomości</label>
                <select
                  className='form-input'
                  value={userMessageType}
                  onChange={(e) => setUserMessageType(e.target.value)}
                >
                  <option value='info'>Informacja</option>
                  <option value='warning'>Ważne</option>
                  <option value='alert'>Alert</option>
                </select>
              </div>

              {/* Message Subject */}
              <div className='user-message-section'>
                <label className='form-label'>Temat wiadomości</label>
                <input 
                  type='text'
                  className='form-input'
                  placeholder='Np. Ważna informacja'
                  value={userMessageSubject}
                  onChange={(e) => setUserMessageSubject(e.target.value)}
                />
              </div>

                {/* Message Content */}
                <div className='user-message-section'>
                  <label className='form-label'>Treść</label>
                  <textarea 
                    className='form-textarea'
                    placeholder='Treść wiadomości...'
                    value={userMessageContent}
                    onChange={(e) => setUserMessageContent(e.target.value)}
                    rows={6}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className='user-selector-footer'>
                <button 
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowUserMessageModal(false);
                    setUserMessageUsers([]);
                    setUserMessageSubject('');
                    setUserMessageContent('');
                    setUserMessageType('info');
                    setUserMessageSearchTerm('');
                  }}
                >
                  Anuluj
                </button>
                <button 
                  className='btn btn-primary'
                  onClick={async () => {
                    if (!userMessageSubject.trim() || !userMessageContent.trim()) {
                      alert('Proszę wypełnić temat i treść wiadomości.');
                      return;
                    }
                    if (userMessageUsers.length === 0) {
                      alert('Proszę wybrać przynajmniej jednego użytkownika.');
                      return;
                    }

                    try {
                      const token = localStorage.getItem('token');
                      const headers = { 'Content-Type': 'application/json' };
                      if (token) {
                        headers.Authorization = `Bearer ${token}`;
                      }

                      const payload = {
                        subject: userMessageSubject,
                        content: userMessageContent,
                        messageType: 'notification',
                        message_type: userMessageType,
                        selectedUsers: userMessageUsers,
                        sendOptions: {}
                      };

                      const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/send`, {
                        method: 'POST',
                        headers: headers,
                        credentials: 'include',
                        body: JSON.stringify(payload)
                      });

                      if (response.ok) {
                        const data = await response.json();
                        alert(`Wiadomość została wysłana pomyślnie do ${data.recipientsCount} odbiorców!`);
                        setShowUserMessageModal(false);
                        setUserMessageUsers([]);
                        setUserMessageSubject('');
                        setUserMessageContent('');
                        setUserMessageType('info');
                        setUserMessageSearchTerm('');
                        fetchMessages();
                      } else {
                        const errorData = await response.json();
                        alert(errorData.error || 'Błąd podczas wysyłania wiadomości.');
                      }
                    } catch (error) {
                      alert('Błąd podczas wysyłania wiadomości.');
                    }
                  }}
                  disabled={userMessageUsers.length === 0}
                >
                  Wyślij ({userMessageUsers.length})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
